import _ from "lodash";
import Select from "react-select";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Col, FormGroup, Row } from "reactstrap";
import {
  USER_COMPANIES,
  USER_EXPAND_CREATE_FORM,
  USER_EXPAND_EDIT_FORM,
} from "../../../../constants/modal.constant";
import { useGlobalCore } from "../../../../cores/globals";
import { useMeCore } from "../../../../cores/me";
import { usePlanCore } from "../../../../cores/plan";
import { useResellerCore } from "../../../../cores/reseller";
import { useUserCore } from "../../../../cores/user";
import {
  SortDirection,
  UserSortField,
} from "../../../../cores/user/interfaces/api/get-users";
import { UserStatus } from "../../../../cores/user/constants/user-status";
import { useIntl } from "react-intl";
import SideModal from "../../../layout/SideModal";
import { InputWembii } from "../../InputWembii";
import { PaginationWemmbii } from "../../pagination/Pagination";
import { PanelWembii } from "../../PanelWembii";
import { CreateUserForm } from "../CreateUserForm";
import { LabelWembii } from "../../LabelWembii";

const userRolesOptions = [
  { value: "", label: "Todos"   },
  { value: "User", label: "User" },
  { value: "Owner", label: "Owner" },
  { value: "Admin", label: "Admin" },
  { value: "Reseller", label: "Reseller" },
];

const searchOptions = [
  { value: "email__p", label: "Email" },
  { value: "firstName__p", label: "Nombre" },
  { value: "lastName__p", label: "Apellido" },
];

type DatePeriod = "" | "1d" | "7d" | "30d" | "90d" | "365d";

const datePeriodOptions: { value: DatePeriod; labelId: string }[] = [
  { value: "", labelId: "app.adminUsers.all" },
  { value: "1d", labelId: "app.users.period24Hours" },
  { value: "7d", labelId: "app.users.period7Days" },
  { value: "30d", labelId: "app.users.period30Days" },
  { value: "90d", labelId: "app.users.period90Days" },
  { value: "365d", labelId: "app.users.period365Days" },
];

const dateFromPeriod = (period: DatePeriod) => {
  if (!period) return undefined;
  const days = Number(period.replace("d", ""));
  return new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();
};

export type UsersBarProps = {
  usersView?: "cards" | "table";
  onUsersViewChange?: (view: "cards" | "table") => void;
  selectedUserIds: string[];
  onClearSelection: () => void;
  sortBy: UserSortField;
  sortDirection: SortDirection;
};

export const UsersBar = ({
  usersView = "table",
  onUsersViewChange,
  selectedUserIds,
  onClearSelection,
  sortBy,
  sortDirection,
}: UsersBarProps) => {
  const intl = useIntl();
  const { getUsers, users, putUser } = useUserCore();
  const { company, user } = useMeCore();
  const { plans, getAllPlans, assignPlan } = usePlanCore();
  const { turnOffModal, isModal, getModalContent, getModalVisibility } = useGlobalCore();
  const { resellers, getResellers } = useResellerCore();
  const [search, setSearch] = useState("");
  const [debounceSearchText, setDebounceSearchText] = useState("");
  const [searchOption, setSearchOption] = useState("email__p");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [role, setRole] = useState("");
  const [lastLoginPeriod, setLastLoginPeriod] = useState<DatePeriod>("");
  const [createdPeriod, setCreatedPeriod] = useState<DatePeriod>("");
  const [reseller, setReseller] = useState(user?.reseller?.id);
  const [selectedPlans, setSelectedPlans] = useState<{ value: string; label: string }[]>(
    [],
  );
  const [options, setOptions] = useState<{ value: string; label: string }[]>([]);
  const [bulkPlanId, setBulkPlanId] = useState("");
  const [bulkWorking, setBulkWorking] = useState(false);

  useEffect(() => {
    setOptions(plans.map((plan) => ({ value: `${plan.id}`, label: intl.formatMessage({ id: plan.name }) })));
  }, [plans, intl]);

  const debouncedSearch = useRef(
    _.debounce((search) => setSearch(search), 1000)
  ).current;
  const lastLoginFrom = useMemo(
    () => dateFromPeriod(lastLoginPeriod),
    [lastLoginPeriod]
  );
  const createdFrom = useMemo(
    () => dateFromPeriod(createdPeriod),
    [createdPeriod]
  );

  useEffect(() => {
    getResellers();
  }, [getResellers]);

  useEffect(() => {
    debouncedSearch(debounceSearchText);
  }, [debounceSearchText, debouncedSearch]);

  useEffect(() => {
    getUsers({
      page,
      perPage,
      [searchOption]: search,
      role,
      plans: selectedPlans.map((plan) => plan.value),
      belongToResellerId: reseller,
      lastLoginSince: lastLoginFrom,
      createdSince: createdFrom,
      sortBy,
      sortDirection,
    });
  }, [
    company,
    createdFrom,
    getUsers,
    page,
    perPage,
    selectedPlans,
    lastLoginFrom,
    reseller,
    role,
    search,
    searchOption,
    sortBy,
    sortDirection,
  ]);

  useEffect(() => {
    setPage(1);
    setPerPage(10);
  }, [
    company,
    createdFrom,
    getUsers,
    lastLoginFrom,
    role,
    selectedPlans,
    reseller,
    search,
    searchOption,
    sortBy,
    sortDirection,
  ]);

  useEffect(() => {}, [
    company,
    createdFrom,
    getUsers,
    lastLoginFrom,
    role,
    selectedPlans,
    reseller,
    search,
    searchOption,
  ]);

  useEffect(() => {
    getAllPlans();
  }, [getAllPlans]);

  useEffect(() => {
    if (user && user.reseller) setReseller(`${user.reseller.id}`);
  }, [resellers, user]);

  const onSuccess = useCallback(() => {
    turnOffModal(USER_EXPAND_EDIT_FORM);
    turnOffModal(USER_EXPAND_CREATE_FORM);
    getUsers({
      page,
      perPage,
      [searchOption]: search,
      role,
      plans: selectedPlans.map((plan) => plan.value),
      belongToResellerId: reseller,
      lastLoginSince: lastLoginFrom,
      createdSince: createdFrom,
      sortBy,
      sortDirection,
    });
  }, [
    turnOffModal,
    createdFrom,
    getUsers,
    lastLoginFrom,
    page,
    perPage,
    searchOption,
    search,
    role,
    selectedPlans,
    reseller,
    sortBy,
    sortDirection,
  ]);

  const refreshUsers = useCallback(() => {
    getUsers({
      page,
      perPage,
      [searchOption]: search,
      role,
      plans: selectedPlans.map((plan) => plan.value),
      belongToResellerId: reseller,
      lastLoginSince: lastLoginFrom,
      createdSince: createdFrom,
      sortBy,
      sortDirection,
    });
  }, [
    getUsers,
    createdFrom,
    lastLoginFrom,
    page,
    perPage,
    reseller,
    role,
    search,
    searchOption,
    selectedPlans,
    sortBy,
    sortDirection,
  ]);

  const onBulkAssignPlan = useCallback(async () => {
    if (!bulkPlanId || selectedUserIds.length === 0) return;
    setBulkWorking(true);
    try {
      await Promise.all(selectedUserIds.map((userId) => assignPlan(userId, bulkPlanId)));
      onClearSelection();
      refreshUsers();
    } finally {
      setBulkWorking(false);
    }
  }, [assignPlan, bulkPlanId, onClearSelection, refreshUsers, selectedUserIds]);

  const onBulkDelete = useCallback(async () => {
    if (selectedUserIds.length === 0) return;
    const confirmed = window.confirm(
      intl.formatMessage(
        {
          id: "app.users.bulk.deleteConfirm",
          defaultMessage:
            "¿Eliminar {count} usuarios? Se marcarán como eliminados.",
        },
        { count: selectedUserIds.length }
      )
    );
    if (!confirmed) return;

    setBulkWorking(true);
    try {
      await Promise.all(
        selectedUserIds.map((userId) =>
          putUser(userId, {
            status: UserStatus.DELETED.id,
          })
        )
      );
      onClearSelection();
      refreshUsers();
    } finally {
      setBulkWorking(false);
    }
  }, [intl, onClearSelection, putUser, refreshUsers, selectedUserIds]);

  const setPlans = useCallback((value) => {
    if (value) {
      setSelectedPlans(value);
    } else {
      setSelectedPlans([]);
    }
  }, []);

  return (
    <>
      <PanelWembii
        className="users-filters-panel w-100 px-4 py-3 my-3"
        shadow
      >
        <Row>
          <Col xs="7" sm="6" lg="3" md="6">
            <InputWembii
              mb={false}
              label={intl.formatMessage({ id: "app.adminUsers.search" })}
              placeholder={intl.formatMessage({ id: "app.adminUsers.search" })}
              type="text"
              onChange={(value) => setDebounceSearchText(value)}
              value={debounceSearchText}
              style={{ borderRadius: "0px 10px 10px 0px" }}
              prepend={
                <InputWembii
                  type="select"
                  onChange={(value: string) => setSearchOption(value)}
                  value={searchOption}
                  mb={false}
                  style={{ borderRadius: "10px 0px 0px 10px" }}
                >
                  {searchOptions.map(({ label, value }) => (
                    <option key={label} value={value}>
                      {label}
                    </option>
                  ))}
                </InputWembii>
              }
            />
          </Col>
          <Col xs="5" lg="3" sm="6" md="6">
            <div className=" mx-auto">
              <InputWembii
                label="Rol"
                type="select"
                onChange={(value: string) => setRole(value)}
                value={role}
              >
                {userRolesOptions.map(({ label, value }) => (
                  <option key={label} value={value}>
                    {label}
                  </option>
                ))}
              </InputWembii>
            </div>
          </Col>
          <Col xs="6" lg="3" sm="6" md="6">
            <div className="mx-auto">
              <InputWembii
                label={intl.formatMessage({ id: "app.users.lastLoginFilter" })}
                type="select"
                onChange={(value: string) =>
                  setLastLoginPeriod(value as DatePeriod)
                }
                value={lastLoginPeriod}
              >
                {datePeriodOptions.map(({ value, labelId }) => (
                  <option key={value || "all"} value={value}>
                    {intl.formatMessage({ id: labelId })}
                  </option>
                ))}
              </InputWembii>
            </div>
          </Col>
          <Col xs="6" lg="3" sm="6" md="6">
            <div className="mx-auto">
              <InputWembii
                label={intl.formatMessage({ id: "app.users.createdFilter" })}
                type="select"
                onChange={(value: string) =>
                  setCreatedPeriod(value as DatePeriod)
                }
                value={createdPeriod}
              >
                {datePeriodOptions.map(({ value, labelId }) => (
                  <option key={value || "all"} value={value}>
                    {intl.formatMessage({ id: labelId })}
                  </option>
                ))}
              </InputWembii>
            </div>
          </Col>
          <Col xs="6" lg="4" sm="6" md="6"> 
            <div className="mx-auto">
              <FormGroup className="d-flex flex-column position-relative w-100 m-0">
                <LabelWembii>
                  Plan
                </LabelWembii>
                <Select
                  isMulti
                  id="plan"
                  name="plans"
                  options={options}
                  value={selectedPlans}
                  className=""
                  onChange={setPlans}
                  placeholder={intl.formatMessage({ id: "app.source.all" })}
                  classNamePrefix="select"
                />
              </FormGroup>
            </div>
          </Col>
          <Col xs="6" lg="3" sm="6" md="6">
            <div className="mx-auto">
              <InputWembii
                label="Reseller"
                type="select"
                onChange={(value: string) => setReseller(value)}
                value={reseller}
                disabled={user?.userRole?.reseller}
              >
                {[{ id: "", name: intl.formatMessage({ id: "app.adminUsers.all" }) },
                  ...resellers].map(({ id, name }) => (
                    <option key={id} value={id}>
                      {name}
                    </option>
                  )
                )}
              </InputWembii>
            </div>
          </Col>
          <Col
            xs="12"
            lg="auto"
            className="d-flex align-items-end justify-content-end mb-2"
          >
            {onUsersViewChange && (
              <div className="d-flex align-items-center flex-wrap users-bar-view-toggle">
                <span className="text-muted small font-weight-bold mb-0 mr-2">
                  {intl.formatMessage({ id: "app.common.view" })}
                </span>
                <div className="view-style-buttons">
                  <Button
                    type="button"
                    className="lbutton"
                    outline={usersView === "cards"}
                    color="transparent"
                    onClick={() => onUsersViewChange("cards")}
                    aria-pressed={usersView === "cards"}
                    aria-label={intl.formatMessage({ id: "app.common.view" })}
                  >
                    <FontAwesomeIcon icon={["fas", "table-cells-large"]} />
                  </Button>
                  <Button
                    type="button"
                    className="rbutton"
                    outline={usersView === "table"}
                    color="transparent"
                    onClick={() => onUsersViewChange("table")}
                    aria-pressed={usersView === "table"}
                    aria-label={intl.formatMessage({ id: "app.common.view" })}
                  >
                    <FontAwesomeIcon icon={["fas", "table-list"]} />
                  </Button>
                </div>
              </div>
            )}
          </Col>
          <Col />
          <Col xs="12" lg="auto">
            <PaginationWemmbii
              className="w-fit mx-auto mr-lg-0 ml-lg-auto"
              page={page}
              setPage={setPage}
              perPage={perPage}
              setPerPage={setPerPage}
              disabledPages={users.length < perPage}
            />
          </Col>
          {usersView === "table" && selectedUserIds.length > 0 && (
            <Col xs="12">
              <div className="d-flex flex-wrap align-items-end justify-content-between mt-3 pt-2 border-top">
                <div className="font-weight-bold mb-2 mb-lg-0">
                  {intl.formatMessage(
                    {
                      id: "app.users.bulk.selectedCount",
                      defaultMessage: "{count} usuarios seleccionados",
                    },
                    { count: selectedUserIds.length }
                  )}
                </div>
                <div className="d-flex flex-wrap align-items-end" style={{ gap: "0.75rem" }}>
                  {(user?.userRole?.admin || user?.userRole?.reseller) && (
                    <FormGroup className="m-0" style={{ minWidth: 220 }}>
                      <LabelWembii>
                        {intl.formatMessage({
                          id: "app.users.bulk.planLabel",
                          defaultMessage: "Cambiar plan",
                        })}
                      </LabelWembii>
                      <InputWembii
                        type="select"
                        mb={false}
                        value={bulkPlanId}
                        onChange={(value: string) => setBulkPlanId(value)}
                      >
                        <option value="">
                          {intl.formatMessage({
                            id: "app.users.bulk.planPlaceholder",
                            defaultMessage: "Selecciona un plan",
                          })}
                        </option>
                        {plans.map(({ id, name }) => (
                          <option key={id} value={String(id)}>
                            {intl.formatMessage({ id: name })}
                          </option>
                        ))}
                      </InputWembii>
                    </FormGroup>
                  )}
                  {(user?.userRole?.admin || user?.userRole?.reseller) && (
                    <Button
                      color="primary"
                      disabled={!bulkPlanId || bulkWorking}
                      onClick={onBulkAssignPlan}
                    >
                      {intl.formatMessage({
                        id: "app.users.bulk.applyPlan",
                        defaultMessage: "Aplicar plan",
                      })}
                    </Button>
                  )}
                  {user?.userRole?.admin && (
                    <Button color="danger" outline disabled={bulkWorking} onClick={onBulkDelete}>
                      {intl.formatMessage({
                        id: "app.users.bulk.delete",
                        defaultMessage: "Eliminar seleccionados",
                      })}
                    </Button>
                  )}
                  <Button color="secondary" outline disabled={bulkWorking} onClick={onClearSelection}>
                    {intl.formatMessage({
                      id: "app.users.bulk.clear",
                      defaultMessage: "Limpiar selección",
                    })}
                  </Button>
                </div>
              </div>
            </Col>
          )}
        </Row>
      </PanelWembii>
      <SideModal
        active={isModal(USER_EXPAND_EDIT_FORM)}
        visible={getModalVisibility(USER_EXPAND_EDIT_FORM)}
        onClose={onSuccess}
      >
        {getModalContent(USER_EXPAND_EDIT_FORM)}
      </SideModal>
      <SideModal
        active={isModal(USER_EXPAND_CREATE_FORM)}
        visible={getModalVisibility(USER_EXPAND_CREATE_FORM)}
        onClose={() => turnOffModal(USER_EXPAND_CREATE_FORM)}
      >
        <CreateUserForm onCreate={onSuccess} />
      </SideModal>
      <SideModal
        active={isModal(USER_COMPANIES)}
        visible={getModalVisibility(USER_COMPANIES)}
        onClose={() => turnOffModal(USER_COMPANIES)}
        left
      >
        {getModalContent(USER_COMPANIES)}
      </SideModal>
    </>
  );
};
